import * as React from 'react';
import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    useReactTable,
    Table as TanStackTable,
} from '@tanstack/react-table';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui';
import { FetchDataOptions, Filters } from '@/types/table.types';
import { cn } from '@/lib/utils';
import { DataTablePagination } from '../data-table-pagination';

interface DataTableProps<TData> {
    columns: ColumnDef<TData>[];
    data: TData[];
    pageCount: number;
    totalCount: number;
    onFetchData: (options: FetchDataOptions) => void;
    isLoading: boolean;
    DataTableToolbar?: (props: {
        table: TanStackTable<TData>;
        globalFilter: string;
        setGlobalFilter: (value: string) => void;
        filters: Filters;
        setFilters: (filters: Filters) => void;
        data: TData[];
        handleRefreshTable: () => void;
    }) => JSX.Element;
    cellPading?: boolean;
    pagination?: boolean;
    initialGlobalFilter?: string;
    initialFilters?: Filters;
    onTableInstance?: (table: TanStackTable<TData>) => void;
}

export function DataTable<TData>({
    columns,
    data,
    pageCount,
    totalCount,
    onFetchData,
    isLoading,
    DataTableToolbar,
    cellPading = true,
    pagination = true,
    initialGlobalFilter = '',
    initialFilters = {},
    onTableInstance,
}: DataTableProps<TData>) {
    const [rowSelection, setRowSelection] = React.useState({});
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = React.useState(initialGlobalFilter);
    const [filters, setFilters] = React.useState<Filters>(initialFilters);
    const [{ pageIndex, pageSize }, setPagination] = React.useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const handleRefreshTable = () => {
        fetchDataRef.current(fetchOptions);
    };

    const enhancedData = data?.map((row) => ({
        ...row,
        refreshTable: handleRefreshTable,
    }));

    const isInitialRender = React.useRef(true);
    const fetchDataRef = React.useRef(onFetchData);

    React.useEffect(() => {
        fetchDataRef.current = onFetchData;
    }, [onFetchData]);

    const fetchOptions = React.useMemo(() => {
        return {
            pageIndex,
            pageSize,
            sorting,
            filters,
            globalFilter,
        };
    }, [pageIndex, pageSize, sorting, JSON.stringify(filters), globalFilter]);

    React.useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            fetchDataRef.current(fetchOptions);
            return;
        }

        const handler = setTimeout(() => {
            fetchDataRef.current(fetchOptions);
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [fetchOptions]);

    const table = useReactTable({
        data: enhancedData,
        columns,
        pageCount,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
            columnFilters,
            pagination: {
                pageIndex,
                pageSize,
            },
            globalFilter,
        },
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
    });

    React.useEffect(() => {
        if (onTableInstance) {
            onTableInstance(table);
        }
    }, [table, onTableInstance]);

    return (
        <div>
            {DataTableToolbar && (
                <DataTableToolbar
                    table={table}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    filters={filters}
                    setFilters={setFilters}
                    data={data}
                    handleRefreshTable={handleRefreshTable}
                />
            )}

            <div className="rounded-md border">
                <Table>
                    <TableHeader className="bg-white-blue-100">
                        {table.getHeaderGroups()?.map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers?.map((header) => (
                                    <TableHead key={header.id} className="font-bold">
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns?.length} className="h-24 text-center">
                                    جاري التحميل...
                                </TableCell>
                            </TableRow>
                        ) : table.getRowModel().rows?.length ? (
                            table.getRowModel().rows?.map((row) => (
                                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                                    {row.getVisibleCells()?.map((cell) => (
                                        <TableCell
                                            className={cn('', {
                                                'p-4': cellPading,
                                                'p-0': !cellPading,
                                            })}
                                            key={cell.id}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns?.length} className="h-24 text-center">
                                    لا توجد بيانات متاحة
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {pagination && <DataTablePagination table={table} totalCount={totalCount} />}
        </div>
    );
}
